/**
 * Field Registry for Custom Report Builder
 * Defines all available fields that can be selected when building reports
 */

// Calculator method IDs for filtering fields by client's assigned calculator
const M_409A = 'private_company_409a';
const M_FUND = 'fund_investor_capital_account';
const M_CONTRIB = 'contributions_accruals';
const M_INCOME = 'income_allocations';
const M_TVPI = 'tvpi_fund';
const M_VALBASIS = 'valuation_plus_basis';

export const AVAILABLE_FIELDS = {
	// Shareholder-level fields (aggregationLevel: 'shareholder')
	shareholder: [
		{ key: 'user_name', label: 'Shareholder Name', type: 'text', source: 'user.user_name' },
		{ key: 'userid', label: 'Email', type: 'text', source: 'user.userid' },
		{ key: 'friendly_name', label: 'Display Name', type: 'text', source: 'user.friendly_name' },
		{ key: 'status', label: 'Status', type: 'text', source: 'userClient.status' },
		{ key: 'tags', label: 'Tags', type: 'array', source: 'userClient.tags' },
		{ key: 'nthRoundUID', label: 'Nth Round UID', type: 'text', source: 'user.userid' },
		{ key: 'phone', label: 'Phone', type: 'text', source: 'userClient.phone' },
		{ key: 'address', label: 'Address', type: 'text', source: 'userClient.address' },
		{ key: 'city', label: 'City', type: 'text', source: 'userClient.city' },
		{ key: 'state', label: 'State', type: 'text', source: 'userClient.state' },
		{ key: 'zip', label: 'Zip', type: 'text', source: 'userClient.zip' },
		{ key: 'country', label: 'Country', type: 'text', source: 'userClient.country' },
		{ key: 'entityType', label: 'Entity Type', type: 'text', source: 'userClient.entity_type' },
		{ key: 'entityName', label: 'Entity Name', type: 'text', source: 'userClient.notes' },
		{ key: 'idNumber', label: 'ID Number', type: 'text', source: 'userClient.idNumber' },
		{ key: 'dob', label: 'Date of Birth', type: 'date', source: 'userClient.dob' },
		{ key: 'emp_status', label: 'Employment Status', type: 'text', source: 'userClient.emp_status' },
		{ key: 'terminationDate', label: 'Termination Date', type: 'date', source: 'userClient.emp_termination_date' },
		{ key: 'taxResidenceState', label: 'Tax Residence State', type: 'text', source: 'userClient.taxResidenceState' },
		{ key: 'createdAt', label: 'Created At', type: 'date', source: 'userClient.createdAt' },
		{ key: 'lastUpdated', label: 'Last Updated', type: 'date', source: 'userClient.lastUpdated' },
		{ key: 'mailOnlyShareholder', label: 'Mail Only', type: 'boolean', source: 'userClient.mailOnlyShareholder' },
		{ key: 'lastSeen', label: 'Last Seen', type: 'date', source: 'calculator:lastSeen', format: 'date' },
		{ key: 'invitedAt', label: 'Invited', type: 'date', source: 'calculator:invitedAt', format: 'date' },
		{ key: 'aliasCount', label: 'Alias Count', type: 'number', source: 'calculator:aliasCount', format: 'number' },
		{ key: 'aliasString', label: 'Aliases (other accounts that this user can see)', type: 'text', source: 'calculator:aliasString' },
		{ key: 'observerCount', label: 'Observer Count', type: 'number', source: 'calculator:observerCount', format: 'number' },
		{ key: 'observerString', label: 'Observers (other users who can see this account)', type: 'text', source: 'calculator:observerString' },
		{ key: 'docCount', label: 'Document Count', type: 'number', source: 'calculator:docCount', format: 'number' },
		{ key: 'docString', label: 'Documents', type: 'text', source: 'calculator:docString' },
	],

	// Share class-level fields (aggregationLevel: 'shareClass')
	shareClass: [
		{ key: 'className', label: 'Class Name', type: 'text', source: 'classConfig.name' },
		{ key: 'cusip', label: 'CUSIP', type: 'text', source: 'classConfig.cusip', nullable: true },
		{ key: 'authorized', label: 'Authorized', type: 'number', source: 'classConfig.authorized', nullable: true },
	],

	// Holdings (current or historical)
	holdings: [
		{ key: 'owned', label: 'Shares Owned', type: 'number', source: 'shares.owned', format: 'number' },
		{ key: 'approved', label: 'Shares Approved', type: 'number', source: 'shares.approved', format: 'number' },
		{ key: 'committed', label: 'Shares Committed', type: 'number', source: 'shares.committed', format: 'number' },
		{ key: 'ownership_percent', label: 'Ownership %', type: 'percent', source: 'calculator:ownershipPercent', format: 'percent' },
		{ key: 'balanceAtDate', label: 'Balance at Date', type: 'number', source: 'calculator:balanceAtDate', format: 'number' },
	],

	// Calculated metrics (use existing calculators)
	// methods: null = all calculators; array = only listed calculators
	calculated: [
		{ key: 'taxBasis', label: 'Tax Basis', type: 'currency', source: 'calculator:taxBasis', format: 'currency', methods: [M_INCOME] },
		{ key: 'capitalAccount', label: 'Capital Account', type: 'currency', source: 'calculator:capitalAccount', format: 'currency', methods: [M_FUND, M_TVPI] },
		{ key: 'totalValue', label: 'Total Value', type: 'currency', source: 'calculator:totalValue', format: 'currency' },
		{ key: 'vestedValue', label: 'Vested Value', type: 'currency', source: 'calc.vestedValue', format: 'currency' },
		{ key: 'unvestedValue', label: 'Unvested Value', type: 'currency', source: 'calc.unvestedValue', format: 'currency' },
		{ key: 'vestedShares', label: 'Vested Shares', type: 'number', source: 'calculator:vestedShares', format: 'number', methods: [M_409A, M_VALBASIS] },
		{ key: 'unvestedShares', label: 'Unvested Shares', type: 'number', source: 'calculator:restrictedShares', format: 'number', methods: [M_409A, M_VALBASIS] },
		{ key: 'restrictedShares', label: 'Restricted Shares', type: 'number', source: 'calculator:restrictedShares', format: 'number', methods: [M_409A, M_VALBASIS] },
		{ key: 'cagr', label: 'CAGR', type: 'percent', source: 'calc.cagr', format: 'percent' },
	],

	// Fund metrics (available when client uses fund/TVPI calculator)
	fundMetrics: [
		{ key: 'tvpi', label: 'TVPI', type: 'number', source: 'calc.fundMetrics.tvpi', format: 'number', methods: [M_FUND, M_TVPI] },
		{ key: 'dpi', label: 'DPI', type: 'number', source: 'calc.fundMetrics.dpi', format: 'number', methods: [M_FUND, M_TVPI] },
		{ key: 'rvpi', label: 'RVPI', type: 'number', source: 'calc.fundMetrics.rvpi', format: 'number', methods: [M_FUND, M_TVPI] },
		{ key: 'moic', label: 'MOIC', type: 'number', source: 'calc.fundMetrics.moic', format: 'number', methods: [M_FUND, M_TVPI] },
	],

	// Event aggregations (within date range)
	events: [
		{ key: 'contributions', label: 'Contributions', type: 'currency', source: 'aggregate:Contribution', format: 'currency' },
		{ key: 'distributions', label: 'Distributions', type: 'currency', source: 'aggregate:Distribution', format: 'currency' },
		{ key: 'incomeAllocations', label: 'Income Allocations', type: 'currency', source: 'aggregate:Gain (Loss) Accrual', format: 'currency' },
		{ key: 'cashDividends', label: 'Cash Dividends', type: 'currency', source: 'aggregate:Cash Dividend', format: 'currency' },
		{ key: 'returnOfCapital', label: 'Return of Capital', type: 'currency', source: 'aggregate:Return of Capital', format: 'currency' },
	],

	// Class-level aggregations (for shareClass reports like BW)
	classAggregations: [
		{ key: 'issued', label: 'Issued', type: 'number', source: 'calculator:sumBalances', format: 'number' },
		{ key: 'treasury', label: 'Treasury', type: 'number', source: 'calculator:sumTreasury', format: 'number' },
		{ key: 'outstanding', label: 'Outstanding', type: 'number', source: 'calculator:outstandingShares', format: 'number' },
		{ key: 'activeAccounts', label: 'Active Accounts', type: 'number', source: 'calculator:countNonTreasury', format: 'number' },
		{ key: 'authorizedUnissued', label: 'Authorized Unissued', type: 'number', source: 'calculator:authorizedUnissued', format: 'number' },
	],

	// Transaction-level fields (aggregationLevel: 'transaction')
	transaction: [
		{ key: 'txDate', label: 'Transaction Date', type: 'date', source: 'txDate', format: 'date' },
		{ key: 'txQuantity', label: 'Quantity', type: 'number', source: 'txQuantity', format: 'number' },
		{ key: 'txPrice', label: 'Price Per Share', type: 'currency', source: 'txPrice', format: 'currency' },
		{ key: 'txTotal', label: 'Total Amount', type: 'currency', source: 'expression:txQuantity*txPrice', format: 'currency' },
		{ key: 'txNotes', label: 'Notes/Cert #', type: 'text', source: 'txNotes' },
		{ key: 'txId', label: 'Transaction ID', type: 'text', source: 'txId' },
		{ key: 'shareClass', label: 'Share Class', type: 'text', source: 'shareClass' },
		{ key: 'senderName', label: 'Sender Name', type: 'text', source: 'sender.user_name' },
		{ key: 'senderEmail', label: 'Sender Email', type: 'text', source: 'transaction.sender' },
		{ key: 'senderIdNumber', label: 'Sender ID', type: 'text', source: 'senderClient.idNumber' },
		{ key: 'receiverName', label: 'Receiver Name', type: 'text', source: 'receiver.user_name' },
		{ key: 'receiverEmail', label: 'Receiver Email', type: 'text', source: 'transaction.receiver' },
		{ key: 'receiverIdNumber', label: 'Receiver ID', type: 'text', source: 'receiverClient.idNumber' },
		{ key: 'vendorCode', label: 'Vendor Code', type: 'text', source: 'transaction.vendorCode' },
		{ key: 'securityType', label: 'Security Type', type: 'text', source: 'transaction.securityType' },
		// Receiver details (for stock listing reports)
		{ key: 'receiverEntityName', label: 'Entity Name', type: 'text', source: 'receiverClient.notes' },
		{ key: 'receiverEntityType', label: 'Shareholder Type', type: 'text', source: 'receiverClient.entity_type' },
		{ key: 'receiverAddress', label: 'Address', type: 'text', source: 'receiverClient.address' },
		{ key: 'receiverStatus', label: 'Status', type: 'text', source: 'receiverClient.status' },
	],

	// Event-level fields (aggregationLevel: 'event')
	event: [
		{ key: 'eventDate', label: 'Event Date', type: 'date', source: 'eventDate', format: 'date' },
		{ key: 'eventType', label: 'Event Type', type: 'text', source: 'eventType' },
		{ key: 'eventAmount', label: 'Event Amount', type: 'currency', source: 'eventAmount', format: 'currency' },
		{ key: 'eventNrid', label: 'Event ID', type: 'text', source: 'eventNrid' },
		{ key: 'eventDescription', label: 'Description', type: 'text', source: 'eventDescription' },
		{ key: 'shareClass', label: 'Share Class', type: 'text', source: 'shareClass' },
		{ key: 'className', label: 'Class Name', type: 'text', source: 'classConfig.name' },
	],

	// Shareholder-event fields (aggregationLevel: 'shareholderEvent')
	// Used for dividend reports showing per-shareholder allocations
	shareholderEvent: [
		{ key: 'user_name', label: 'Shareholder Name', type: 'text', source: 'user.user_name' },
		{ key: 'userid', label: 'Email', type: 'text', source: 'userid' },
		{ key: 'entityName', label: 'Entity Name', type: 'text', source: 'userClient.notes' },
		{ key: 'idNumber', label: 'ID Number', type: 'text', source: 'userClient.idNumber' },
		{ key: 'entityType', label: 'Entity Type', type: 'text', source: 'userClient.entity_type' },
		{ key: 'status', label: 'Status', type: 'text', source: 'userClient.status' },
		{ key: 'empStatus', label: 'Employment Status', type: 'text', source: 'userClient.emp_status' },
		{ key: 'dob', label: 'Date of Birth', type: 'date', source: 'userClient.dob', format: 'date' },
		{ key: 'terminationDate', label: 'Termination Date', type: 'date', source: 'userClient.emp_termination_date', format: 'date' },
		{ key: 'eventDate', label: 'Event Date', type: 'date', source: 'eventDate', format: 'date' },
		{ key: 'eventType', label: 'Event Type', type: 'text', source: 'eventType' },
		{ key: 'eventDescription', label: 'Event Description', type: 'text', source: 'eventDescription' },
		{ key: 'shareClass', label: 'Share Class', type: 'text', source: 'shareClass' },
		{ key: 'allocationAmount', label: 'Allocation (Gross)', type: 'currency', source: 'allocationAmount', format: 'currency' },
		{ key: 'allocationNet', label: 'Allocation (Net)', type: 'currency', source: 'allocationNet', format: 'currency' },
		{ key: 'sharesOwned', label: 'Shares Owned', type: 'number', source: 'sharesOwned', format: 'number' },
		{ key: 'perShareAmount', label: 'Per Share Amount', type: 'currency', source: 'expression:allocationAmount/sharesOwned', format: 'currency' },
		{ key: 'eventGrossTotal', label: 'Event Total', type: 'currency', source: 'eventGrossTotal', format: 'currency' },
		{ key: 'allocationPercent', label: 'Allocation %', type: 'percent', source: 'expression:allocationAmount/eventGrossTotal*100', format: 'percent' },
	],
};

/**
 * Per-class field templates — each becomes {ClassName}_{fieldKey}
 * sourced via calc.classes.{ClassName}.{calcField}
 */
const PER_CLASS_FIELD_TEMPLATES = [
	// Core (all calculators)
	{ suffix: 'value', label: 'Value', type: 'currency', calcField: 'value', format: 'currency', methods: null },
	{ suffix: 'sharePrice', label: 'Share Price', type: 'currency', calcField: 'sharePrice', format: 'currency', methods: null },
	{ suffix: 'owned', label: 'Owned', type: 'number', calcField: 'owned', format: 'number', methods: null },
	{ suffix: 'vested', label: 'Vested', type: 'number', calcField: 'vested', format: 'number', methods: null },
	{ suffix: 'vestedValue', label: 'Vested Value', type: 'currency', calcField: 'vestedValue', format: 'currency', methods: null },
	{ suffix: 'unvestedValue', label: 'Unvested Value', type: 'currency', calcField: 'unvestedValue', format: 'currency', methods: null },
	{ suffix: 'strikePrice', label: 'Strike Price', type: 'currency', calcField: 'strikePrice', format: 'currency', methods: [M_409A, M_VALBASIS] },
	{ suffix: 'cagr', label: 'CAGR', type: 'percent', calcField: 'cagr', format: 'percent', methods: null },
	// 409A vesting
	{ suffix: 'totalTarget', label: 'Total Target', type: 'number', calcField: 'totalTarget', format: 'number', methods: [M_409A] },
	{ suffix: 'priceChange', label: 'Price Change', type: 'percent', calcField: 'priceChange', format: 'percent', methods: [M_409A] },
	// Fund / Income / Contributions shared
	{ suffix: 'capitalAccount', label: 'Capital Account', type: 'currency', calcField: 'capitalAccount', format: 'currency', methods: [M_FUND, M_TVPI] },
	{ suffix: 'contributions', label: 'Contributions', type: 'currency', calcField: 'contributions', format: 'currency', methods: [M_FUND, M_CONTRIB, M_INCOME, M_TVPI] },
	{ suffix: 'distributions', label: 'Distributions', type: 'currency', calcField: 'distributions', format: 'currency', methods: [M_FUND, M_CONTRIB, M_INCOME, M_TVPI] },
	{ suffix: 'returnsOfCapital', label: 'Return of Capital', type: 'currency', calcField: 'returnsOfCapital', format: 'currency', methods: [M_FUND, M_TVPI] },
	{ suffix: 'accruals', label: 'Accruals', type: 'currency', calcField: 'accruals', format: 'currency', methods: [M_CONTRIB] },
	// Income allocations
	{ suffix: 'taxBasis', label: 'Tax Basis', type: 'currency', calcField: 'taxBasis', format: 'currency', methods: [M_INCOME] },
	{ suffix: 'incomeAllocations', label: 'Income Allocations', type: 'currency', calcField: 'incomeAllocations', format: 'currency', methods: [M_INCOME] },
	{ suffix: 'marketValue', label: 'Market Value', type: 'currency', calcField: 'marketValue', format: 'currency', methods: [M_INCOME] },
	{ suffix: 'unrealizedGain', label: 'Unrealized Gain', type: 'currency', calcField: 'unrealizedGain', format: 'currency', methods: [M_INCOME] },
	// TVPI Fund
	{ suffix: 'tvpi', label: 'TVPI', type: 'number', calcField: 'tvpi', format: 'number', methods: [M_FUND, M_TVPI] },
	{ suffix: 'userPercentage', label: 'Ownership %', type: 'percent', calcField: 'userPercentage', format: 'percent', methods: [M_TVPI] },
	{ suffix: 'fundNav', label: 'Fund NAV', type: 'currency', calcField: 'fundNav', format: 'currency', methods: [M_TVPI] },
	// Valuation + Basis
	{ suffix: 'valuationValue', label: 'Valuation Value', type: 'currency', calcField: 'valuationValue', format: 'currency', methods: [M_VALBASIS] },
	{ suffix: 'basisAddition', label: 'Basis Addition', type: 'currency', calcField: 'basisAddition', format: 'currency', methods: [M_VALBASIS] },
];

/**
 * Generate per-class calculator fields based on client equity config.
 * Returns FieldRegistry entries for each share class that resolve via
 * calc.classes.{ClassName}.{field} after ensureCalcResult runs.
 *
 * @param {Object} clientData - Client configuration object
 * @returns {Array} Array of field definitions
 */
export function generatePerClassFields(clientData) {
	if (!clientData?.equity || typeof clientData.equity !== 'object') return [];
	const classes = Object.keys(clientData.equity);
	if (!classes.length) return [];

	const method = clientData?.flags?.default_value_method || 'private_company_409a';
	const fields = [];
	classes.forEach((className) => {
		PER_CLASS_FIELD_TEMPLATES.forEach((tmpl) => {
			// Filter: null methods = all calculators; array = only listed calculators
			if (tmpl.methods !== null && !tmpl.methods.includes(method)) return;
			fields.push({
				key: `${className}_${tmpl.suffix}`,
				label: tmpl.label,
				type: tmpl.type,
				source: `calc.classes.${className}.${tmpl.calcField}`,
				format: tmpl.format,
				category: className,
			});
		});
	});
	return fields;
}

/**
 * Generate field definitions for custom columns configured on the cap table.
 * Manual columns (source: 'custom') get rewritten to 'custom:{key}' so they
 * can resolve through resolveColumnValue. Non-manual columns keep their original source.
 *
 * @param {Object} clientData - Client configuration object
 * @returns {Array} Array of field definitions
 */
export function generateCustomColumnFields(clientData) {
	const cols = clientData?.cap_table_custom_columns;
	if (!Array.isArray(cols) || !cols.length) return [];
	// Collect all static field keys to avoid duplicating registry fields
	const staticKeys = new Set();
	for (const category of Object.values(AVAILABLE_FIELDS)) {
		category.forEach((f) => staticKeys.add(f.key));
	}
	return cols
		.filter((col) => {
			// Skip columns that duplicate a static registry field
			if (staticKeys.has(col.key)) return false;
			// Skip per-class fields (they're generated by generatePerClassFields)
			if (col.source?.startsWith('calc.classes.')) return false;
			return true;
		})
		.map((col) => ({
			key: col.key,
			label: col.label || col.key,
			type: col.format || col.type || 'text',
			source: col.source === 'custom' ? `custom:${col.key}` : col.source,
			format: col.format || 'text',
			category: '_customColumns',
		}));
}

/**
 * Get fields for a specific aggregation level
 * @param {string} aggregationLevel - 'shareholder', 'shareClass', 'transaction', 'event', or 'shareholderEvent'
 * @param {Object} [clientData] - Optional client config; when provided, per-class calculator fields are included
 * @returns {Array} Available fields for that level
 */
export function getFieldsForAggregationLevel(aggregationLevel, clientData) {
	switch (aggregationLevel) {
		case 'shareholder': {
			const method = clientData?.flags?.default_value_method || M_409A;
			const methodFilter = (f) => !f.methods || f.methods.includes(method);
			const fields = [
				...AVAILABLE_FIELDS.shareholder,
				...AVAILABLE_FIELDS.holdings,
				...AVAILABLE_FIELDS.calculated.filter(methodFilter),
				...AVAILABLE_FIELDS.fundMetrics.filter(methodFilter),
				...AVAILABLE_FIELDS.events,
			];
			if (clientData) fields.push(...generatePerClassFields(clientData));
			if (clientData) fields.push(...generateCustomColumnFields(clientData));
			return fields;
		}

		case 'shareClass':
			return [...AVAILABLE_FIELDS.shareClass, ...AVAILABLE_FIELDS.classAggregations];

		case 'transaction':
			return [...AVAILABLE_FIELDS.transaction];

		case 'event':
			return [...AVAILABLE_FIELDS.event];

		case 'shareholderEvent':
			return [...AVAILABLE_FIELDS.shareholderEvent];

		default:
			return [];
	}
}

/**
 * Get all available fields grouped by category
 * @returns {Object} Fields grouped by category
 */
export function getAllFieldsGrouped() {
	return AVAILABLE_FIELDS;
}

/**
 * Group an array of fields into ordered categories.
 * Per-class fields (those with field.category set) are grouped as "{ClassName} Holdings"
 * and placed after the Holdings section. Returns { groups: OrderedMap, perClassCategories: Set }.
 * @param {Array} fields - Array of field definitions
 * @returns {{ groups: Object, perClassCategories: Set<string> }}
 */
export function groupFieldsByCategory(fields) {
	const grouped = {};
	const collapsibleCategories = new Set();
	fields.forEach((field) => {
		let category;
		if (field.category === '_customColumns') {
			category = 'Custom Columns';
			collapsibleCategories.add(category);
		} else if (field.category) {
			category = `${field.category} Holdings`;
			collapsibleCategories.add(category);
		} else {
			category = 'Other';
			if (field.source?.includes('user.') || field.source?.includes('userClient.')) category = 'Shareholder Info';
			else if (field.source?.includes('classConfig.')) category = 'Share Class Info';
			else if (field.source?.includes('shares.') || field.source?.includes('calculator:balance') || field.source?.includes('calculator:ownership'))
				category = 'Holdings';
			else if (field.source?.startsWith('aggregate:')) category = 'Event Totals';
			else if (field.source?.startsWith('calculator:') || field.source?.startsWith('calc.')) category = 'Calculated Fields';
			else if (field.source?.startsWith('expression:')) category = 'Derived Values';
			else if (field.source?.includes('receiver.') || field.source?.includes('receiverClient.')) category = 'Receiver Info';
			else if (field.source?.includes('sender.') || field.source?.includes('senderClient.')) category = 'Sender Info';
			else if (field.source?.includes('transaction.') || field.key?.startsWith('tx')) category = 'Transaction Details';
			else if (field.source?.includes('event') || field.key?.startsWith('event')) category = 'Event Details';
			else if (field.source?.includes('allocation')) category = 'Allocation Details';
		}
		if (!grouped[category]) grouped[category] = [];
		grouped[category].push(field);
	});
	// Order: Shareholder Info, Custom Columns, Holdings, per-class holdings, then remaining
	const standardOrder = ['Shareholder Info'];
	const ordered = {};
	standardOrder.forEach((key) => { if (grouped[key]) ordered[key] = grouped[key]; });
	if (grouped['Custom Columns']) ordered['Custom Columns'] = grouped['Custom Columns'];
	if (grouped['Holdings']) ordered['Holdings'] = grouped['Holdings'];
	collapsibleCategories.forEach((key) => { if (key !== 'Custom Columns' && grouped[key]) ordered[key] = grouped[key]; });
	Object.keys(grouped).forEach((key) => { if (!ordered[key]) ordered[key] = grouped[key]; });
	return { groups: ordered, collapsibleCategories };
}

/**
 * Get field definition by key
 * @param {string} key - Field key
 * @param {Object} [clientData] - Optional client config; when provided, also searches per-class fields
 * @returns {Object|null} Field definition or null if not found
 */
export function getFieldByKey(key, clientData) {
	for (const category of Object.values(AVAILABLE_FIELDS)) {
		const field = category.find((f) => f.key === key);
		if (field) return field;
	}
	// Search dynamic per-class fields when clientData is available
	if (clientData) {
		const perClassFields = generatePerClassFields(clientData);
		const field = perClassFields.find((f) => f.key === key);
		if (field) return field;
	}
	// Search custom column fields
	if (clientData) {
		const customFields = generateCustomColumnFields(clientData);
		const field = customFields.find((f) => f.key === key);
		if (field) return field;
	}
	return null;
}

/**
 * Format types for display
 */
export const FORMAT_TYPES = {
	text: { label: 'Text', formatter: (val) => val || '' },
	number: { label: 'Number', formatter: (val) => (typeof val === 'number' ? val.toLocaleString('en-US') : val) },
	currency: {
		label: 'Currency',
		formatter: (val) => (typeof val === 'number' ? val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val),
	},
	percent: { label: 'Percent', formatter: (val) => (typeof val === 'number' ? val.toFixed(4) + '%' : val) },
	date: { label: 'Date', formatter: (val) => (val ? moment(val).format('MM/DD/YYYY') : '') },
	array: { label: 'List', formatter: (val) => (Array.isArray(val) ? val.join(', ') : val) },
};

/**
 * Get formatter for a format type
 * @param {string} formatType - Format type name
 * @returns {Function} Formatter function
 */
export function getFormatter(formatType) {
	return FORMAT_TYPES[formatType]?.formatter || FORMAT_TYPES.text.formatter;
}

